import Theme from "@/components/Theme";
import { IS_LIVE } from "@/utils/constants";
import Hero from "@/organisms/Hero";
import GrowBusiness from "@/components/organisms/GrowBusiness";
import Slider from "@/components/organisms/Slider";
import AudioLayout from "@/organisms/AudioLayout";
import Discover from "@/organisms/Discover";
import H2 from "@/components/atoms/text/H2";
import IndustryCard from "@/components/molecules/CardLink";
import IndustryPill from "@/components/molecules/PillLink";
import {
  HvacIcon,
  RestorationIcon,
  HomeServicesIcon,
  PlumbingIcon,
  ConstructionIcon,
  ElectricalIcon,
} from "@/atoms/Icons";
import Trustpilot from "@/organisms/Trustpilot";

export default function Integration() {
  const seoData = {
    indexing: true,
    title: "24/7 Live Answering for Field Service Industries - AnswerForce",
    description:
      "Answering services for residential, commercial field services, and home services contractors. Make your business 24/7 and never miss another call.",
    og: {
      title: "24/7 Live Answering for Field Service Industries - AnswerForce",
      description:
        "Answering services for residential, commercial field services, and home services contractors. Make your business 24/7 and never miss another call.",
      url: "https://www.answerforce.com/industries",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/industry-og.jpg",
    },
    twitter: {
      title: "24/7 Live Answering for Field Service Industries - AnswerForce",
      description:
        "Answering services for residential, commercial field services, and home services contractors. Make your business 24/7 and never miss another call.",
      url: "https://www.answerforce.com/industries",
      image:
        "https://assets.answerforce.com/answerforce/us/website/images/og/industry-og.jpg",
      image_alt: "AnswerForce industries page",
    },
  };

  const heroImgData = {
    fileName: "industry-hero",
    alt: "Industry service provider",
  };

  const hero = (
    <Hero
      heroTitle="Built for all types of service businesses<span class='dot'>.</span>"
      heroContent="Industry solutions, customized for your business. Deliver on quality<br class='phone-hide' /> customer service and build trust with your customers."
      heroImgData={heroImgData}
      hasPricingCta
      hasTrustpilot
    />
  );

  const triggerChat = (e) => {
    e.preventDefault();
    const chatwidgetElem = document.querySelector(".chatsupport-button");
    chatwidgetElem && chatwidgetElem.click();
  };

  const SliderData = [
    {
      title: "Services",
      content:
        "Services built to support your growth and scale as your needs change.",
      list: [
        "24/7 call and chat answering",
        "After-hours answering",
        "Lead qualification",
        "Bilingual answering",
      ],
      hasCta: true,
      cta: {
        href: "/services",
        text: "View all services",
      },
      image: {
        fileName: "industry-services-chat",
        altName: "Man with laptop",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
    {
      hasMobileAppImg: true,
      title: "Tools",
      content:
        "Powerful tools to connect your prospects, clients, and team with your business.",
      list: [
        "Mobile app",
        "Live chat",
        "Business phone line",
        "Appointment scheduling",
      ],
      hasCta: true,
      cta: {
        href: "/tools",
        text: "View all tools",
      },
      image: {
        fileName: "tools-mobile-app",
        altName: "mobile app",
        isAlignBottom: true,
        desktopWidth: "500",
      },
    },
    {
      title: "Integrations",
      content:
        "Connect AnswerForce receptionists to your job management software, CRM tools, marketing platforms, and more.",
      list: ["ServiceTitan", "Housecall Pro", "Workiz", "Zapier"],
      hasCta: true,
      cta: {
        href: "/integrations",
        text: "View all integrations",
      },
      image: {
        fileName: "integrations-grid",
        altName: "integration icons",
        isAlignBottom: false,
        desktopWidth: "500",
      },
    },
  ];

  const industryCardsData = [
    {
      icon: HvacIcon,
      title: "HVAC",
      content:
        "Scalable services for different seasons. Be  there 24/7 when the air con breaks.",
      href: "/hvac-answering-service",
    },
    {
      icon: RestorationIcon,
      title: "Restoration",
      content:
        "Restore peace of mind to customers in their time of need by answering 24/7.",
      href: "/restoration-answering-service",
    },
    {
      icon: HomeServicesIcon,
      title: "Home services",
      content:
        "Be there for homeowners day and night. Answer emergency calls, book estimates.",
      href: "/home-services",
    },
    {
      icon: PlumbingIcon,
      title: "Plumbing",
      content: "Answer emergency callouts and book estimates 24/7.",
      href: "/plumbing-answering-service",
    },
    {
      icon: ConstructionIcon,
      title: "Contractors",
      content: "Access information when you are on-site or in the office.",
      href: "/contractors-answering-service",
    },
    {
      icon: ElectricalIcon,
      title: "Electrical",
      content:
        "Always answer when the power goes out. Grow your business with professional.",
      href: "/electrician-answering-service",
    },
  ];

  const industryPillsData = [
    {
      text: "Roofing",
      href: "/industries/roofing",
    },
    {
      text: "Landscaping",
      href: "/landscaping-answering-service",
    },
    {
      text: "Construction",
      href: "/industries/construction",
    },
    {
      text: "Flooring",
      href: "/industries/flooring",
    },
    {
      text: "Garage door",
      href: "/industries/garage-door",
    },
    {
      text: "Cleaning",
      href: "/cleaning-answering-service",
    },
    {
      text: "Handyman",
      href: "/industries/handyman",
    },
    {
      text: "Septic",
      href: "/home-services",
    },
    {
      text: "Pest control",
      href: "/industries/pest-control",
    },
    {
      text: "Locksmith",
      href: "/industries/locksmith",
    },
    {
      text: "Solar",
      href: "/industries/solar",
    },
    {
      text: "Remodeling",
      href: "/remodeling-answering-service",
    },
    {
      text: "Appliance repair",
      href: "/industries/appliance-repair",
    },
    {
      text: "Property maintenance",
      href: "/industries/property-maintenance",
    },
    {
      text: "Painting",
      href: "/industries/painting",
    },
    {
      text: "Small business",
      href: "/answering-services/small-business",
    },
  ];

  return (
    <Theme mainClassName="main" hasSidebar hero={hero} seo={seoData}>
      <section className="bc-white pv-15-d pv-8">
        <div className="container">
          <H2 className="bold mb-8-d mb-7 ta-center">
            Find a solution for your industry<span className="dot">.</span>
          </H2>
          <IndustryCard data={industryCardsData} />
          <IndustryPill data={industryPillsData} />
        </div>
      </section>
      <GrowBusiness
        title="Working in a different industry?"
        cta={{
          href: "#",
          text: "Chat to our team",
        }}
        onClick={triggerChat}
      />
      <Slider
        data={SliderData}
        variant="solutions"
        title="Plans that include more<span class='dot'>.</span>"
        subTitle="Get a plan that's built to give you more time and flexibility, support your clients,<br class='desktop-only' /> and work with your processes."
      />
      <AudioLayout
        title="Hear how <br class='desktop-only'>we <br class='phone-only'>sound"
        image={{
          fileName: "refer-friend-female-receptionist",
          altText: "Female receptionist",
          desktopWidth: 348,
          mobileWidth: 308,
        }}
      />
      <Trustpilot title="In our customers&#8217; words" variant="carousel" />
      <Discover
        bgColor="white"
        title="Begin your journey to 24/7 growth<span class='dot'>.</span>"
        color="black"
        cta="See Prices"
        href={
          IS_LIVE
            ? "https://www.answerforce.com/register"
            : "https://testing.answerforce.com/register"
        }
      />
    </Theme>
  );
}
